import { FC, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Context } from '..'
import { LOCALES } from '../core/i18n/locales'
import {
	COOKIE_CONSENT_STORAGE_KEY,
	COOKIE_SETTINGS_EVENT,
	enableAnalytics,
	getCookieConsent,
} from '../core/utils/analyticsConsent'
import styles from '../styles/CookieConsent.module.scss'

const CookieConsent: FC = () => {
	const { store, studioStore } = useContext(Context)
	const [isVisible, setIsVisible] = useState(false)
	const [isCustomizing, setIsCustomizing] = useState(false)
	const [analyticsAllowed, setAnalyticsAllowed] = useState(false)
	// The UAE storefront is English even when an old browser locale is still persisted.
	const isRussian = store.locale === LOCALES.RUSSIAN && Number(studioStore?.selCountry?.id) !== 9
	const t = (ru: string, en: string) => (isRussian ? ru : en)

	useEffect(() => {
		const consent = getCookieConsent()
		setAnalyticsAllowed(consent === 'accepted')
		if (consent === 'accepted') enableAnalytics()
		if (consent === null) setIsVisible(true)

		const openSettings = () => {
			setAnalyticsAllowed(getCookieConsent() === 'accepted')
			setIsCustomizing(true)
			setIsVisible(true)
		}
		window.addEventListener(COOKIE_SETTINGS_EVENT, openSettings)
		return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, openSettings)
	}, [])

	const saveConsent = (accepted: boolean) => {
		const previousConsent = getCookieConsent()
		localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, accepted ? 'accepted' : 'declined')
		setIsVisible(false)

		if (accepted) {
			enableAnalytics()
		} else if (previousConsent === 'accepted') {
			window.location.reload()
		}
	}

	if (!isVisible) return null

	return (
		<section className={styles.banner} role='dialog' aria-label={t('Настройки cookies', 'Cookie settings')} aria-live='polite'>
			<p>
				{t(
					'Мы используем файлы cookie для работы сайта, анализа трафика и персонализации. Вы можете принять все файлы cookie или настроить их категории. Подробнее — в нашей ',
					'We use cookies to operate the website, analyze traffic and personalize content. You can accept all cookies or configure their categories. Learn more in our '
				)}
				<Link to='/cookies'>{t('Политике использования cookie', 'Cookie Policy')}</Link>.
			</p>
			{isCustomizing && (
				<div className={styles.settings}>
					<label><input type='checkbox' checked disabled /> {t('Необходимые cookie', 'Necessary cookies')}</label>
					<label><input type='checkbox' checked={analyticsAllowed} onChange={(event) => setAnalyticsAllowed(event.target.checked)} /> {t('Аналитические cookie (Яндекс.Метрика)', 'Analytics cookies (Yandex Metrica)')}</label>
				</div>
			)}
			<div className={styles.actions}>
				{isCustomizing ? (
					<button type='button' className={styles.primary} onClick={() => saveConsent(analyticsAllowed)}>{t('Сохранить настройки', 'Save settings')}</button>
				) : (
					<>
						<button type='button' className={`${styles.secondary} ${styles.wideAction}`} onClick={() => saveConsent(false)}>{t('Только необходимые', 'Necessary only')}</button>
						<button type='button' className={styles.secondary} onClick={() => setIsCustomizing(true)}>{t('Настроить', 'Customize')}</button>
						<button type='button' className={`${styles.primary} ${styles.wideAction}`} onClick={() => saveConsent(true)}>{t('Принять все', 'Accept all')}</button>
					</>
				)}
			</div>
		</section>
	)
}

export default observer(CookieConsent)
